<!-- 
Dialog for creating or editing a role.
-->
<template>
  <div>
    <v-dialog ref="dlg" :value="show" persistent content-class="dialog lg">
      <v-form ref="form">
        <v-card>
          <v-toolbar color="secondary" flat dark>
            <v-toolbar-title>{{ title }}</v-toolbar-title>
            <v-spacer/>
            <our-user-guide-button topic="administration-roles-and-permissions"/>
          </v-toolbar>
          <our-busy-indicator :show="busy"/>
          <v-card-text>
            <v-container grid-list-md pa-0>
              <v-layout wrap>
                <v-flex v-if="show" xs6>
                  <v-text-field
                    :value="role.name" :rules="[rules.required]" class="role-name"
                    autofocus label="Name*" @input="update('name', $event)"
                  />
                </v-flex>
                <v-flex xs6>
                  <v-text-field
                    :value="role.prismsName" :rules="[rules.required]" class="prisms-name"
                    autofocus persistent-hint
                    label="PRISMS name*" hint="This must match exactly the role name specified in PRISMS."
                    @input="update('prismsName', $event)"
                  />
                </v-flex>
                <v-flex xs12>
                  <v-container fluid class="pa-0 mt-4 permission-selection">
                    <v-layout row wrap>
                      <v-flex xs12 sm6>
                        <p class="subheading">Available permissions</p>
                        <div ref="available" class="scroll-container" style="max-height: 380px; overflow-y: auto; overflow-x: hidden;">
                          <div
                            v-for="category in categories"
                            :key="'available-category-' + category"
                          >
                            <p v-if="getAvailablePermissions(category).length > 0" class="body-2 mt-3">
                              {{ lookups.permissionCategoryDescriptions[category] }}
                            </p>
                            <div
                              v-for="permission in getAvailablePermissions(category)" 
                              :ref="'sel' + permission"
                              :key="permission"
                            >
                              <v-switch 
                                :input-value="false"
                                :label="lookups.permissionClaimTypeDescriptions[permission]" 
                                color="primary"
                                class="ma-0 pt-0 pl-2"
                                @click="selectPermission(permission, true)"
                              />
                            </div>
                            <v-divider v-if="getAvailablePermissions(category).length > 0"/>
                          </div>
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <p class="subheading">Selected permissions</p>
                        <div ref="selected" class="scroll-container" style="max-height: 380px; overflow-y: auto; overflow-x: hidden;">
                          <div
                            v-for="category in categories"
                            :key="'selected-category-' + category"
                          >
                            <p v-if="getSelectedPermissions(category).length > 0" class="body-2 mt-3">
                              {{ lookups.permissionCategoryDescriptions[category] }}
                            </p>
                            <div
                              v-for="(permission) in getSelectedPermissions(category)" 
                              :ref="'sel' + permission"
                              :key="permission"
                            >
                              <v-switch 
                                :input-value="true"
                                :label="lookups.permissionClaimTypeDescriptions[permission]" 
                                color="primary"
                                class="ma-0 pt-0 pl-2"
                                @click="selectPermission(permission, false)"
                              />
                            </div>
                            <v-divider v-if="getSelectedPermissions(category).length > 0"/>
                          </div>
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-flex>
              </v-layout>
            </v-container>
            <our-required-field-indicator/>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
            <v-btn 
              ref="save-button" :disabled="busy" flat 
              color="success" type="submit" @click.prevent="save"
            >Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </div>
</template>

<script>
import _ from "underscore";
import * as action from "@/store/roles/action-types";
import * as mutation from "@/store/roles/mutation-types";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    roleId: {
      type: Number,
      default: null
    },
    create: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.roles.edit;
    },
    role() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    title() {
      if (this.state.isNew) {
        return "Add role";
      } else {
        return !this.state.loading ? `Edit role '${this.role.name}'` : "Edit role";
      }
    },
    permissionClaimTypes() {
      return this.lookups.permissionClaimTypes;
    },
    selectedPermissions() {
      return _.sortBy(this.role.permissions, t => this.lookups.permissionClaimTypeDescriptions[t]);
    },
    availablePermissions() {
      var available = this.lookups.permissionClaimTypes.filter(t => !this.role.permissions.includes(t.value));
      return _.pluck(available, "value");
    },
    categories() {
      return _(this.lookups.permissionCategories).pluck("value");
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        if (this.create) {
          this.$refs.form.reset();
          this.$store.commit(mutation.EDIT_INITIALISE_FOR_CREATE);
        } else {
          this.$refs.form.reset();
          this.$store.dispatch(action.EDIT_GET, this.roleId);
        }
      }
    }
  },
  methods: {
    getAvailablePermissions(category) {
      const all = this.getAllPermissionsInCategory(category);
      return _(this.availablePermissions).intersection(all);
    },
    getSelectedPermissions(category) {
      const all = this.getAllPermissionsInCategory(category);
      return _(this.selectedPermissions).intersection(all);
    },
    getAllPermissionsInCategory(category) {
      const permissions = _(this.lookups.permissionClaimTypes).filter(
        p => p.attributeData.permissionCategory.category == category
      );
      return _(permissions).pluck("value");
    },
    selectPermission(permission, adding) {
      var currentSelected = this.selectedPermissions.slice();
      if (adding) {
        currentSelected.push(permission);
      } else {
        currentSelected = _.reject(currentSelected, t => t === permission);
      }
      this.update("permissions", currentSelected);

      this.$nextTick(() => {
        const container = this.$refs[adding ? "selected" : "available"];
        const target = this.$refs[`sel${permission}`];
        this.$vuetify.goTo(target[0].offsetTop - container.offsetTop, { container });
      });
    },
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_SAVE))) {
        this.hide();
        if (this.create) {
          this.$router.push(`/roles/${this.role.id}`);
        }
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>

<style lang="scss">
.permission-selection {
  .v-input__slot {
    margin-bottom: 2px !important;
  }
  .v-input--selection-controls .v-input__control .v-input__slot .v-label {
    font-size: 13px !important;
  }
}
</style>